import React, { useState } from 'react';
import EmptyState from '../../../components/shared/EmptyState';
import './RevenueArtistCards.css';

const PLACEHOLDER_AVATAR = 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&q=80&w=400&h=400';

const ROLE_COLORS = {
  Singer: { bg: 'rgba(11,102,227,0.08)', color: '#0B66E3' },
  Lyrics: { bg: '#f0fdf4', color: '#166534' },
  Music:  { bg: '#eff6ff', color: '#1e40af' }
};

function ArtistCardSkeleton() {
  return (
    <div className="rac-card rac-skeleton">
      <div className="rac-avatar-box rac-skeleton-block" />
      <div className="rac-body">
        <div className="rac-skeleton-line" style={{ width: '65%', height: 14 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <div className="rac-skeleton-line" style={{ width: 52, height: 18, borderRadius: 4 }} />
          <div className="rac-skeleton-line" style={{ width: 52, height: 18, borderRadius: 4 }} />
        </div>
        <div className="rac-skeleton-line" style={{ width: '85%', height: 42, borderRadius: 8 }} />
      </div>
      <div className="rac-footer-row">
        <div className="rac-skeleton-line" style={{ width: '55%', height: 12 }} />
      </div>
    </div>
  );
}

function RevenueArtistCards({ items, isLoading, onSelectArtist, searchQuery }) {
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 16;

  const filteredItems = searchQuery
    ? items.filter(a => (a.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : items;

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const startIdx = (currentPage - 1) * perPage;
  const pageItems = filteredItems.slice(startIdx, startIdx + perPage);

  return (
    <div className="rac-container">
      {isLoading ? (
        <div className="rac-grid">
          {Array.from({ length: 8 }).map((_, i) => <ArtistCardSkeleton key={i} />)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="rac-empty-wrap">
          <EmptyState title="No Artists Found" subtitle="No artist revenue records match your search." hasSearch={!!searchQuery} />
        </div>
      ) : (
        <>
          <div className="rac-grid">
            {pageItems.map((artist, idx) => {
              const avatarUrl = artist.avatar || artist.image_url || PLACEHOLDER_AVATAR;
              const types = artist.types || [];
              return (
                <div key={artist.id || idx} className="rac-card" onClick={() => onSelectArtist?.(artist)}>
                  {/* Avatar */}
                  <div className="rac-avatar-box">
                    <img
                      src={avatarUrl}
                      alt={artist.name}
                      className="rac-avatar-img"
                      onError={e => { e.target.src = PLACEHOLDER_AVATAR; }}
                    />
                  </div>

                  {/* Body */}
                  <div className="rac-body">
                    <h3 className="rac-name">{artist.name}</h3>

                    {types.length > 0 && (
                      <div className="rac-roles-row">
                        {types.map(t => (
                          <span key={t} className="rac-role-pill" style={{ background: ROLE_COLORS[t]?.bg, color: ROLE_COLORS[t]?.color }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="rac-metrics-grid">
                      <div className="rac-metric-box">
                        <span className="rac-metric-label">Songs</span>
                        <span className="rac-metric-val">{artist.total_songs || 0}</span>
                      </div>
                      <div className="rac-metric-box income-box">
                        <span className="rac-metric-label">Income</span>
                        <span className="rac-metric-val">{artist.income || '$0.00'}</span>
                      </div>
                      <div className="rac-metric-box earn-box">
                        <span className="rac-metric-label">Earning</span>
                        <span className="rac-metric-val">{artist.earning || '$0.00'}</span>
                      </div>
                      <div className="rac-metric-box out-box">
                        <span className="rac-metric-label">Outgoing</span>
                        <span className="rac-metric-val">{artist.outgoing || '$0.00'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="rac-footer-row">
                    <button type="button" className="rac-view-btn">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="rac-pagination-row">
              <span className="rac-pagination-info">
                {startIdx + 1}–{Math.min(startIdx + perPage, filteredItems.length)} of {filteredItems.length}
              </span>
              <div className="rac-pagination-btns">
                <button className="rac-pager-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>&laquo;</button>
                <button className="rac-pager-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>&lsaquo;</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => Math.abs(p - currentPage) <= 2 || p === 1 || p === totalPages)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && <span className="rac-pager-ellipsis">…</span>}
                      <button
                        className={`rac-pager-btn${currentPage === p ? ' active' : ''}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button className="rac-pager-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>&rsaquo;</button>
                <button className="rac-pager-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>&raquo;</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RevenueArtistCards;
